import React, { useEffect } from 'react'
import { Button, PendingIcon } from '../../lib/components'
import './WithdrawalNoticeModal.scss'

type WithdrawalNoticeModalBaseProps = {
  isOpen: boolean
  isSubmitting?: boolean
  onClose: () => void
  testId: string
}

type WithdrawalNoticeModalProps =
  | (WithdrawalNoticeModalBaseProps & {
      variant: 'confirm'
      onConfirm: () => void
    })
  | (WithdrawalNoticeModalBaseProps & {
      variant: 'success'
      /** Defaults to the redemption success subtitle when omitted. */
      subtitle?: string
      /**
       * Notice body copy. Defaults to the redemption 24h cancellation note.
       * Pass `null` to hide the notice box.
       */
      notice?: string | null
    })

const DEFAULT_SUCCESS_SUBTITLE = 'Your redemption request has been raised successfully.'
const CANCELLATION_NOTICE =
  'Please note that this request may be cancelled within 24 hours of submission. Once this window has passed, the redemption cannot be cancelled.'

export const WithdrawalNoticeModal: React.FC<WithdrawalNoticeModalProps> = (props) => {
  const { variant, isOpen, isSubmitting = false, onClose, testId } = props

  const handleConfirm = () => {
    if (props.variant === 'confirm') props.onConfirm()
  }
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen) return null

  const isConfirm = variant === 'confirm'
  const successSubtitle =
    props.variant === 'success' ? props.subtitle ?? DEFAULT_SUCCESS_SUBTITLE : null
  const successNotice =
    props.variant === 'success'
      ? props.notice === undefined
        ? CANCELLATION_NOTICE
        : props.notice
      : CANCELLATION_NOTICE

  return (
    <div
      className='withdrawal-notice-modal__backdrop'
      role='presentation'
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose()
      }}
    >
      <div
        className='withdrawal-notice-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby={`${testId}-title`}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className='withdrawal-notice-modal__header'>
          <p className='withdrawal-notice-modal__eyebrow'>
            {isConfirm ? 'Redemption request' : 'Success'}
          </p>
          <h2 id={`${testId}-title`} className='withdrawal-notice-modal__title'>
            {isConfirm ? 'Confirm submission' : 'Request submitted'}
          </h2>
          {!isConfirm && successSubtitle ? (
            <p className='withdrawal-notice-modal__subtitle'>{successSubtitle}</p>
          ) : null}
        </header>

        {isConfirm || successNotice ? (
          <div className='withdrawal-notice-modal__body'>
            <div className='withdrawal-notice-modal__notice'>
              <PendingIcon
                classes={['withdrawal-notice-modal__notice-icon']}
                width='20'
                height='20'
              />
              <p>{isConfirm ? CANCELLATION_NOTICE : successNotice}</p>
            </div>
          </div>
        ) : null}

        <footer className='withdrawal-notice-modal__footer'>
          {isConfirm ? (
            <>
              <Button
                text='Go back'
                variant='secondary-btn'
                handleClick={onClose}
                isDisabled={isSubmitting}
                testId='withdrawal-cancellation-notice-back-btn'
              />
              <Button
                text={isSubmitting ? 'Submitting…' : 'Confirm request'}
                variant='primary-btn'
                handleClick={handleConfirm}
                isDisabled={isSubmitting}
                testId='withdrawal-cancellation-notice-confirm-btn'
              />
            </>
          ) : (
            <Button
              text='Done'
              variant='primary-btn'
              handleClick={onClose}
              testId='withdrawal-success-notice-ok-btn'
            />
          )}
        </footer>
      </div>
    </div>
  )
}
